import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CATEGORIES, BY_ID, type CategoryId } from "../lib/categories.ts";
import { endSession, openBlock, type Block, type TrackerState } from "../lib/tracker.ts";
import { recordEnd, recordTap } from "../lib/record.ts";
import { loadSession, mutateSession, requestDurableStorage, type LocalSession } from "../lib/queue.ts";
import { drain } from "../lib/sync.ts";
import { waitingRows } from "../lib/prune.ts";
import { createTapper } from "../lib/tapper.ts";
import { clock, mergeBlocks, observedSeconds, sessionSummary, type SessionSummary } from "../lib/summary.ts";
import type { RecordingLock } from "../lib/recordingLock.ts";

const newId = () => crypto.randomUUID();
const NOTE_FAILED = "That note did not save. Try again.";
class NoteStillOpen extends Error {}

/** Which block a note belongs to is decided when the sheet opens, not when it
    is saved. The sheet leaves most of the grid tappable, and a tap made while
    typing used to move the note onto the block that tap opened. */
interface NoteTarget { key: string; categoryId: CategoryId | null; eventId: string | null }

// The screen an observer actually uses, standing in a food court, one-handed,
// while trying not to look like they are watching someone.
//
// Two rules shape everything here:
//   1. Nothing is acknowledged on screen until it is in IndexedDB. Each tap's
//      save is awaited before the screen moves, in lib/tapper.ts.
//   2. All fifteen targets are visible at once. Scrolling to find a category
//      costs seconds of misattributed time.
export default function Tracker({
  session, initial, onEnded, recording,
}: { session: LocalSession; initial: TrackerState; onEnded: (summary: SessionSummary) => void; recording: RecordingLock }) {
  const assertWritable = (stored: LocalSession) => {
    recording.assert();
    if(stored.endedAt!==null || stored.observerId!==session.observerId) throw new Error("This session is no longer recording.");
  };
  const [state, setState] = useState<TrackerState>(initial);
  // What the next tap is computed from. React state is for drawing, and a tap
  // that arrives before a re-render commits used to compute from the previous
  // screen: it reused a block number, and every later batch failed on it.
  const stateRef = useRef<TrackerState>(initial);
  // Blocks recorded before this screen opened that have since uploaded and
  // left the queue: without them a resumed session's totals start again.
  const earlier = useRef<Block[]>(session.history ?? []);
  const [now, setNow] = useState(Date.now());
  const [waiting, setWaiting] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Notes waiting to be written, oldest first. A second Other tapped while the
  // first note is still open waits its turn: it used to re-point the open sheet,
  // and the text already typed was saved against the second block.
  const [notes, setNotes] = useState<NoteTarget[]>([]);
  const noteFor = notes[0] ?? null;
  // How many notes are open or queued, known at once rather than at the next
  // render. Ending checks it inside the tap queue, after any queued Other has
  // landed, so a session cannot end ahead of a required note it was owed.
  // A set of keys rather than a count, so a double tap on Cancel cannot close
  // one note twice and leave another open note uncounted.
  const openNotes = useRef(new Set<string>());
  const openNote = (target: NoteTarget) => { openNotes.current.add(target.key); setNotes((q) => [...q, target]); };
  const closeNote = (key: string) => {
    openNotes.current.delete(key);
    setNotes((q) => q.filter((t) => t.key !== key));
    // A failure about this note must not greet the next one.
    setProblem((p) => (p === NOTE_FAILED ? null : p));
  };
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [ending, setEnding] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  // When the problem line appeared. A tap clears it only once it has been up
  // long enough to read: in a burst the next tap came 100 ms later and wiped
  // "did not save" before anyone saw it.
  const problemSince = useRef(0);
  const report = (message: string) => { problemSince.current = Date.now(); setProblem(message); };

  // Still asked for, because a persisted store is what keeps unsent taps safe
  // on iOS. No longer shown: an amber "storage not persisted" on every screen
  // read as a fault and told the observer nothing they could act on (UX-1).
  useEffect(() => { void requestDurableStorage(); }, []);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const s = await loadSession(session.id);
      if (!s) throw new Error("Session storage is missing");
      setWaiting(waitingRows(s)); setUploadError(s.lastError);
    } catch { setWaiting(null); setUploadError("Cannot read this phone’s storage. Upload status is unknown. Keep this page open and try again."); }
  }, [session.id]);
  useEffect(() => {
    void refresh();
    const changed = () => { void refresh(); };
    addEventListener("field-observation-queue", changed);
    addEventListener("field-observation-queue-remote", changed);
    return () => { removeEventListener("field-observation-queue", changed); removeEventListener("field-observation-queue-remote", changed); };
  }, [refresh]);

  // When the tracker was last on screen. A session abandoned by a reload or a
  // dead phone is closed at this time, not at whenever the app is next opened.
  useEffect(() => {
    const beat = () => {
      void mutateSession(session.id, null, (s) => { assertWritable(s); return { ...s, lastSeenAt: Date.now() }; })
        .then(refresh).catch(() => undefined);
    };
    beat();
    const t = setInterval(beat, 15000);
    return () => clearInterval(t);
  }, [session.id, refresh]);

  // Opportunistic. The app-wide loop keeps trying in the background, so a
  // failure here is expected in a dead zone and is not shown as a problem.
  const trySync = useCallback(async () => {
    if (!navigator.onLine) return;
    await drain(session.id, session.observerId).catch(() => undefined);
    await refresh();
  }, [session.id, session.observerId, refresh]);

  const tapper = useMemo(() => createTapper({
    key: `taps:${session.id}`,
    current: () => stateRef.current,
    newId,
    // Through mutateSession, never a bare load-then-save: the sync path
    // mutates this same record, and a close written here was once clobbered
    // by a drain that had read the session a moment earlier.
    persist: async (next, outcome, at) => {
      const saved = await mutateSession(session.id, null, (stored) => { assertWritable(stored); return recordTap(stored, next, outcome, at); });
      if (!saved) throw new Error("session record missing");
      setWaiting(waitingRows(saved));
    },
    commit: (next, outcome) => {
      stateRef.current = next;
      setState(next);
      setNow(Date.now());
      // The problem line is not cleared here. A tap queued behind a failed one
      // used to wipe "That tap did not save" milliseconds after it appeared, so
      // a lost tap in a burst went unseen. It clears when the observer taps again.
      if (outcome.kind === "opened" && BY_ID.get(outcome.block.categoryId)!.requiresNote) {
        openNote({ key: newId(), categoryId: outcome.block.categoryId, eventId: outcome.block.id });
      }
      void trySync();
    },
  }), [session, trySync]);

  function onTap(categoryId: CategoryId) {
    if (ending) return;
    setConfirmEnd(false);
    if (Date.now() - problemSince.current > 3000) setProblem(null);
    // Stamped now, not when its turn in the queue comes.
    tapper.tap(categoryId, Date.now()).catch(() => {
      // It did not save, and the screen has not moved. Say so plainly, and
      // which one, rather than let the observer believe a tap that never happened.
      report(`That tap on ${BY_ID.get(categoryId)!.label} did not save. Tap it again.`);
    });
  }

  async function addNote(body: string, target: NoteTarget): Promise<boolean> {
    const note = { id: newId(), eventId: target.eventId, categoryId: target.categoryId, notedAt: Date.now(), body };
    try {
      const saved = await mutateSession(session.id, null, (stored) => {
        assertWritable(stored);
        return { ...stored, pendingNotes: [...stored.pendingNotes, note] };
      });
      if (!saved) throw new Error("session record missing");
      // closeNote also clears this note's own failure message, if a retry worked.
      closeNote(target.key);
      setWaiting(waitingRows(saved));
      void trySync();
      return true;
    } catch {
      report(NOTE_FAILED);
      return false;
    }
  }

  async function finish() {
    // A note still open lives only on screen; ending now would lose it, and
    // Other's note is required. End is disabled meanwhile, and this holds too.
    if (ending || openNotes.current.size > 0) return;
    setEnding(true);
    try {
      // Behind every tap already made, so none of them lands after the end.
      const summary = await tapper.after(async () => {
        // A queued Other may have landed since the button was pressed, and
        // opened a required note on the way.
        if (openNotes.current.size > 0) throw new NoteStillOpen();
        const at = Date.now();
        const { state: next, closed } = endSession(stateRef.current, at);
        const saved=await mutateSession(session.id, null, (stored) => { assertWritable(stored); return recordEnd(stored, closed, at); });
        if(!saved)throw new Error("Session storage is missing");
        stateRef.current = next;
        setState(next);
        return sessionSummary(mergeBlocks(earlier.current, next.blocks), at);
      });
      // Not awaited. It used to hold the observer on "Ending..." for half a
      // minute on bad signal; the app-wide loop keeps sending after this.
      // Do not open a request while explicitly offline: some engines leave
      // that request pending until its deadline, delaying the online retry.
      if (navigator.onLine) void drain(session.id, session.observerId).catch(() => undefined);
      recording.assert();
      onEnded(summary);
    } catch (e) {
      report(e instanceof NoteStillOpen
        ? "Finish the note first, then end the session."
        : "The session did not end. Tap End session again.");
      setEnding(false);
      setConfirmEnd(false);
    }
  }

  const all = useMemo(() => mergeBlocks(earlier.current, state.blocks), [state.blocks]);
  const summary = sessionSummary(all, now);
  const tables = new Map(summary.rows.map((r) => [r.categoryId, r.taps]));
  const current = openBlock(state);
  const started = all.length > 0;

  return (
    <div className="tracker">
      {/* Upload state, not the browser's online flag: that flag said "online"
          through a dead zone while nothing was uploading (UX-3). */}
      <header className="status">
        <span className={waiting === null || waiting > 0 ? "queue warn" : "queue"} role="status">
          {waiting === null ? "Upload status unknown" : waiting > 0 ? `${waiting} ${waiting === 1 ? "record" : "records"} not uploaded yet` : "All uploaded"}
        </span>
      </header>
      {uploadError && waiting !== 0 && <p className="err">{uploadError} <button onClick={() => { void refresh(); void trySync(); }}>Retry upload</button></p>}

      {/* Load-bearing while a note is open: the sheet repeats this line and
          grows by its height, and this copy pushes the grid down by the same
          amount, which is what keeps the counter tiles clear of the sheet. */}
      {problem && <p className="err">{problem}</p>}

      {/* Total time, which the pilot owners asked for in place of the current
          activity and its count: the lit tile already says what is selected,
          and the counts now sit on the counter tiles themselves. */}
      <div className="current">
        {started ? (
          <>
            <span className="muted">Total time</span>
            <span className="clock">{clock(observedSeconds(all, now))}</span>
          </>
        ) : (
          <strong className="muted">Tap what the cleaner is doing</strong>
        )}
      </div>

      <div className="grid">
        {CATEGORIES.map((c) => {
          const active = current?.categoryId === c.id;
          const count = c.countsTables ? (tables.get(c.id) ?? 0) : null;
          return (
            <button key={c.id}
              className={`cat${active ? " active" : ""}${c.countsTables ? " counter" : ""}`}
              aria-pressed={active}
              aria-label={count === null ? c.label : `${c.label}, ${count} ${count === 1 ? "table" : "tables"}`}
              onClick={() => onTap(c.id)} disabled={ending}>
              <span className="lab">{c.label}</span>
              {count !== null && <span className="count">{count}</span>}
            </button>
          );
        })}
      </div>

      <div className="actions">
        {confirmEnd ? (
          // Swapped sides on purpose: a nervous double-tap on End session lands
          // on Keep going, not on the confirm.
          <>
            <button className="danger" onClick={() => void finish()} disabled={ending || noteFor !== null}>
              {ending ? "Ending..." : "Yes, end session"}
            </button>
            <button disabled={ending} onClick={() => setConfirmEnd(false)}>Keep going</button>
          </>
        ) : (
          <>
            <button onClick={() => {
              if (openNotes.current.size === 0) openNote({ key: newId(), categoryId: current?.categoryId ?? null, eventId: current?.id ?? null });
            }}>
              Add note
            </button>
            {/* Ending cannot be undone, and it used to take one tap (UX-7). */}
            <button className="danger" onClick={() => setConfirmEnd(true)} disabled={ending || noteFor !== null}>
              End session
            </button>
          </>
        )}
      </div>

      {summary.rows.length > 0 && (
        <details className="summary">
          <summary>Breakdown so far</summary>
          <SummaryTable summary={summary} />
        </details>
      )}

      {noteFor !== null && (
        <NoteDialog
          key={noteFor.key}
          target={noteFor}
          required={noteFor.categoryId ? (BY_ID.get(noteFor.categoryId)?.requiresNote ?? false) : false}
          problem={problem}
          onSave={(b) => addNote(b, noteFor)}
          onCancel={() => closeNote(noteFor.key)}
        />
      )}
    </div>
  );
}

/** Time, share of the session and tables per activity, in tile order. Shown
    while tracking and again when the session ends. */
export function SummaryTable({ summary }: { summary: SessionSummary }) {
  return (
    <table className="data">
      <thead>
        <tr><th>Activity</th><th className="n">Time</th><th className="n">Share</th><th className="n">Tables</th></tr>
      </thead>
      <tbody>
        {summary.rows.map((r) => {
          const c = BY_ID.get(r.categoryId)!;
          return (
            <tr key={r.categoryId}>
              <td>{c.label}</td>
              <td className="n">{clock(r.seconds)}</td>
              <td className="n">{(r.share * 100).toFixed(1)}%</td>
              <td className="n">{c.countsTables ? r.taps : ""}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function NoteDialog({ target, required, problem, onSave, onCancel }: {
  target: NoteTarget; required: boolean; problem: string | null;
  onSave: (body: string) => Promise<boolean>; onCancel: () => void;
}) {
  const [body, setBody] = useState("");
  // One save at a time: Enter auto-repeat, or Enter and a tap on Save, used to
  // queue the same note twice.
  const [saving, setSaving] = useState(false);
  const category = target.categoryId;
  const save = () => {
    if (saving || body.trim() === "") return;
    setSaving(true);
    void onSave(body.trim()).then((ok) => { if (!ok) setSaving(false); });
  };
  return (
    <div className="sheet" role="dialog" aria-label="Add a note">
      <div className="sheet-head">
        <p>
          Note{category ? ` on ${BY_ID.get(category)!.label}` : ""}
          {/* The guide is explicit that Other is never recorded without a
              comment, because the post-survey pass reads them looking for a
              recurring category worth splitting out. */}
          {required && <em> (required)</em>}
        </p>
        {!required && <button type="button" className="link" onClick={onCancel}>Cancel</button>}
      </div>
      {/* Repeated here because the sheet sits over the screen's own problem
          line: a tap that failed while a note was open went unseen, which is
          the lost-tap harm the tap queue exists to prevent. */}
      {problem && <p className="err" role="alert">{problem}</p>}
      <div className="note-row">
        {/* Enter saves, as the pilot owners asked; Shift+Enter is a new line. An
            Enter that confirms a Japanese or Chinese conversion is not a save:
            Safari reports it with keyCode 229 rather than isComposing. */}
        <textarea autoFocus value={body} onChange={(e) => setBody(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing && e.nativeEvent.keyCode !== 229) {
                      e.preventDefault(); save();
                    }
                  }}
                  enterKeyHint="done" placeholder="What happened?" rows={2} aria-label="Note" />
        <button className="primary" disabled={saving || body.trim() === ""} onClick={save}>Save</button>
      </div>
    </div>
  );
}
